import { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import TvCard from '@/components/feature/TvCard';
import SEO from '@/components/base/SEO';
import { tvSeries, siteInfo } from '@/mocks/tvSeries';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const showId = Number(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const show = tvSeries.find(s => s.id === showId);

  if (!show) {
    return (
      <div className="min-h-screen bg-background-50">
        <SEO title="剧集未找到 - 草民影院" description="您查找的剧集不存在或已下架" canonicalUrl="https://caomin.tv" />
        <Navbar />
        <main className="pt-20 max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">剧集未找到</h1>
          <p className="text-foreground-600 mb-6">您查找的剧集不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedShows = tvSeries
    .filter(s => s.categorySlug === show.categorySlug && s.id !== show.id)
    .slice(0, 6);

  const pageUrl = `https://caomin.tv/article/${show.id}`;
  const categoryUrl = `https://caomin.tv/category/${show.categorySlug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://caomin.tv',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: show.category,
            item: categoryUrl,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: show.title,
            item: pageUrl,
          },
        ],
      },
      {
        '@type': 'TVSeries',
        name: show.title,
        description: show.description,
        url: pageUrl,
        image: show.coverUrl,
        genre: show.tags,
        containsSeason: {
          '@type': 'TVSeason',
          numberOfEpisodes: show.episodes,
        },
        datePublished: `${show.year}`,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: show.rating,
          bestRating: 10,
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SEO
        title={`${show.title} - ${show.category} | ${siteInfo.name}在线观看`}
        description={`${show.title}剧情简介：${show.description.substring(0, 120)}... 主演：${show.actors}。${siteInfo.name}提供${show.title}全集在线观看，高清不卡即点即看。`}
        keywords={`${show.title},${show.category},${show.title}在线观看,${show.title}全集,${show.actors},电视剧,草民影院`}
        canonicalUrl={pageUrl}
        ogType="video.tv_show"
        ogImage={show.coverUrl}
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: show.category, href: `/category/${show.categorySlug}` },
              { label: show.title },
            ]}
          />
        </div>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="w-full lg:w-[300px] flex-shrink-0">
              <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
                <img
                  src={show.coverUrl}
                  alt={show.title}
                  title={`${show.title} - ${show.category} ${show.year} ${show.episodes}集`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {show.title}
              </h1>

              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 text-sm font-semibold bg-accent-500 text-background-50 rounded-md whitespace-nowrap">
                  评分 {show.rating}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-md whitespace-nowrap ${
                  show.status === '更新中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-background-200 text-foreground-600'
                }`}>
                  {show.status}
                </span>
                <span className="text-sm text-foreground-500">{show.year}</span>
                <span className="text-sm text-foreground-500">共{show.episodes}集</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {show.tags.map(tag => (
                  <span key={tag} className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-md whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-foreground-700 w-14 flex-shrink-0 whitespace-nowrap">导演</span>
                  <span className="text-sm text-foreground-600">{show.director}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-foreground-700 w-14 flex-shrink-0 whitespace-nowrap">主演</span>
                  <span className="text-sm text-foreground-600">{show.actors}</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-foreground-700 w-14 flex-shrink-0 whitespace-nowrap">类型</span>
                  <Link
                    to={`/category/${show.categorySlug}`}
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    {show.category}
                  </Link>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-2">剧情简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{show.description}</p>
              </div>

              <Link
                to={`/category/${show.categorySlug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line text-base"></i>
                </span>
                前往观看
              </Link>
            </div>
          </div>
        </section>

        {relatedShows.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedShows.map(s => (
                  <TvCard key={s.id} {...s} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}